/**
 * Article detail — main pane only. Sidebar lives in ResearchLayout.
 *
 * Phase 3: embedded saved queries render as a "Live Queries" section
 * between the article body and Related Articles. Each embed is its own
 * `ResearchQueryEmbed` instance which executes against the live Explorer
 * pipeline on mount. The filter `valueLookup` (used for chip labels) is
 * fetched once here at the page level and shared across embeds.
 *
 * Layout note: the outer Box uses `px: 2` so the content edges align with
 * the inner content of article cards on the list view (cards have `p: 2`).
 * Without this, navigating list → detail visually shifted the right-edge
 * of content by 16px.
 *
 * Header is always-condensed and sticky — see the StickyArticleHeader
 * comment below for the rationale.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";

import ArticleBody from "./ArticleBody";
import ArticleDetailSkeleton from "./ArticleDetailSkeleton";
import ResearchQueryEmbed from "./ResearchQueryEmbed";
import { useArticle } from "./useArticle";
import { useEmbeddedQueries } from "./useEmbeddedQueries";
import { useResearchUser } from "./Research";
import { useResearchLayoutContext } from "./ResearchLayout";
import { extractHeadings, HEADING_SCROLL_MARGIN_TOP } from "./headings";
import VersionHistoryDrawer from "./Editor/VersionHistoryDrawer";
import {
  buildValueLookup,
  ValueLookup,
} from "../../Elements/ExplorerDataGrid/VideoCellHandler";
import { useLeagueApi } from "../../../data/LeagueEnvironmentProvider";
import { FilterResponse } from "../../../models/Explorer";

// Horizontal padding that matches the inner content alignment of article
// cards on the list view (cards have `p: 2`).
const DETAIL_SX = { px: 2 };

function formatLongDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const api = useLeagueApi();
  const { loading, error, notFound, article } = useArticle(slug);
  const { embeddedQueries } = useEmbeddedQueries(slug);
  const { isAdmin } = useResearchUser();
  const { setTocHeadings } = useResearchLayoutContext();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [filters, setFilters] = useState<FilterResponse | null>(null);

  // Filters for the `valueLookup` shared across embedded query DataGrids.
  // Fetched once at the page level so all embeds see the same chip
  // labels (and we don't re-fetch the static filter list per embed).
  // Player options aren't fetched here — player_id filter chips will
  // render raw UUIDs in v1; we'll layer player options in once the
  // editor lands so we know which player filters an article actually
  // uses.
  useEffect(() => {
    let cancelled = false;
    api
      .getFilters()
      .then((resp) => {
        if (cancelled) return;
        setFilters(resp.data);
      })
      .catch(() => {
        // Non-fatal — the DataGrid renders without chip labels.
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const valueLookup: ValueLookup = useMemo(
    () => buildValueLookup(filters),
    [filters],
  );

  // Drive the sidebar's "In this article" list from the article body.
  // Cleared on unmount so the list view doesn't show stale headings.
  // Append synthetic entries for the post-body sections (Queries, then
  // Related) when they're present. Order here must match the DOM order
  // below so the TOC reads top-to-bottom as the user scrolls.
  const headings = useMemo(() => {
    const out = extractHeadings(article?.body_md ?? "");
    if (embeddedQueries.length > 0) {
      out.push({ id: "live-queries", text: "Queries", level: 1 });
    }
    if ((article?.related_articles.length ?? 0) > 0) {
      out.push({ id: "related-articles", text: "Related", level: 1 });
    }
    return out;
  }, [article?.body_md, embeddedQueries.length, article?.related_articles.length]);
  useEffect(() => {
    setTocHeadings(headings);
    return () => setTocHeadings([]);
  }, [headings, setTocHeadings]);

  if (loading) {
    return <ArticleDetailSkeleton />;
  }

  if (notFound) {
    return (
      <Box sx={DETAIL_SX}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Article not found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The article you're looking for doesn't exist or you don't have
          permission to view it.
        </Typography>
        <Link
          component="button"
          onClick={() => navigate("/research")}
          sx={{ color: "#CF0A2C", textDecoration: "none" }}
        >
          ← Back to all articles
        </Link>
      </Box>
    );
  }

  if (error || !article) {
    return (
      <Box sx={DETAIL_SX}>
        <Alert severity="error">{error ?? "Failed to load article"}</Alert>
      </Box>
    );
  }

  const updatedAt = article.last_edited_at ?? article.published_at;
  const isDraft = article.status === "draft";

  return (
    <Box sx={DETAIL_SX}>
      <StickyArticleHeader
        title={article.title}
        categoryName={article.category?.name}
        categoryColor={article.category?.color}
        isDraft={isDraft}
        visibility={article.visibility}
        authorName={article.author?.name}
        updatedAt={updatedAt}
        isAdmin={isAdmin}
        onHistory={() => setHistoryOpen(true)}
        onEdit={() => navigate(`/research/${article.slug}/edit`)}
      />

      <ArticleBody markdown={article.body_md} />

      {embeddedQueries.length > 0 && (
        <Box
          id="live-queries"
          sx={{
            // Matches the body-heading scroll-margin so this section
            // lands at the same offset below the sticky header chrome
            // when the TOC link is clicked.
            scrollMarginTop: HEADING_SCROLL_MARGIN_TOP,
          }}
        >
          {/* Section header — matches the h1 style in ArticleBody so the
              "Queries · Live" header reads as a peer of the article's
              own top-level section headings, not as a foreign chrome
              element. The h1's own `borderBottom` is the divider; no
              separate <Divider> needed. */}
          <Typography
            component="h2"
            sx={{
              fontSize: "0.85rem",
              fontWeight: 700,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: "text.primary",
              mt: 4,
              mb: 1.5,
              pb: 0.75,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Queries · Live
          </Typography>
          {embeddedQueries.map((embed) => (
            <ResearchQueryEmbed
              key={embed.id}
              embed={embed}
              valueLookup={valueLookup}
            />
          ))}
        </Box>
      )}

      {article.related_articles.length > 0 && (
        <Box
          id="related-articles"
          sx={{ scrollMarginTop: HEADING_SCROLL_MARGIN_TOP }}
        >
          {/* Header matches the h1 typography family from ArticleBody so
              the section reads as a peer of the article's own top-level
              section headings — same treatment as "Queries · Live". */}
          <Typography
            component="h2"
            sx={{
              fontSize: "0.85rem",
              fontWeight: 700,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: "text.primary",
              mt: 4,
              mb: 1.5,
              pb: 0.75,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Related Articles
          </Typography>
          {/* Card grid: 3-up on md+, 2-up on sm, 1-up on xs. Each card
              shows the category name in the category color uppercased
              + the article title in bold. Click anywhere on the card
              to navigate. */}
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
            }}
          >
            {article.related_articles.map((r) => (
              <Box
                key={r.slug}
                onClick={() => navigate(`/research/${r.slug}`)}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  backgroundColor: "#fff",
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  transition: "border-color 120ms ease, background-color 120ms ease",
                  "&:hover": {
                    borderColor: "#bdbdbd",
                    backgroundColor: "rgba(0,0,0,0.02)",
                  },
                }}
              >
                {r.category && (
                  <Typography
                    sx={{
                      // Use the category's own brand color rather than a
                      // flat red so categories stay visually distinct
                      // across the rail.
                      color: r.category.color ?? "#CF0A2C",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      mb: 0.5,
                    }}
                  >
                    {r.category.name}
                  </Typography>
                )}
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    color: "text.primary",
                    lineHeight: 1.35,
                  }}
                >
                  {r.title}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <VersionHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        slug={article.slug}
      />
    </Box>
  );
}

/**
 * Always-condensed sticky header for the article detail page.
 *
 * Replaces the previous multi-row stacked header (breadcrumbs + chip row +
 * h4 title + summary + byline + divider — six rows of chrome). Articles
 * are long and the author/date/edit controls were scrolling out of view
 * within a half-screen, so the header is now two compact lines that stick
 * to the top of the viewport for the entire read.
 *
 *   Line 1:  <title>  [CATEGORY] [DRAFT · vis]              [⟲] [Edit]
 *   Line 2:  By <author> · {Last edited|Published} <date>
 *
 * Back-to-list navigation is intentionally NOT in this header. The
 * sticky category rail's "Research" / "All" / category list is the
 * always-visible nav back to the browse page, so a breadcrumb here would
 * just duplicate it and steal horizontal space from the title.
 *
 * The bar sticks at `top: 24` on md+ so it aligns horizontally with
 * the category rail's sticky offset. Sticking at top:0 would naively
 * solve the "body content flashes through the gap above the bar"
 * problem but loses the alignment with the rail. The fix: a `::before`
 * pseudo-element 24px tall, positioned right above the bar (top:-24),
 * with the same background treatment as the elevated bar. When stuck,
 * the bar + its ::before together cover viewport y=0 to y=bar.bottom
 * with one continuous opaque strip — no gap.
 *
 * The shield uses a pseudo-element rather than a separate sibling
 * sticky Box because a sibling in flow would push the bar's natural
 * position down by 24px (misaligning it from the rail at scroll=0
 * before any stickiness engages). The pseudo-element is positioned
 * absolutely within the sticky bar's box, so it inherits stickiness
 * for free without taking flow space.
 *
 * Layout polish: the bar's background is always opaque (#f5f5f5,
 * matching the page bg, so it's visually invisible at scroll=0). Once
 * stuck, a 1px bottom border + soft diffuse drop shadow fade in to
 * mark the bar as elevated chrome detached from the page. The bg is
 * NOT transitioned — an earlier transparent → opaque transition was
 * exposing body content through the bar during fast scrolls before it
 * finished animating; always-opaque blocks content immediately and the
 * elevation cue is carried entirely by the border + shadow. Action
 * buttons (history + edit) are aligned to the vertical center of the
 * two-line title+byline stack via the outer Stack's `alignItems:
 * 'center'`.
 *
 * Detection is via an `IntersectionObserver` on a zero-height sentinel
 * placed in flow just above the bar. The first attempt used a
 * `window.scroll` listener, which never fired reliably in this app
 * (something between `main { overflow-x: hidden }` and the App-level
 * `height: 100dvh` layout means the document viewport isn't always the
 * scroll source). IO triggers off the sentinel actually crossing the
 * viewport — that's geometric and works regardless of which ancestor
 * owns the scroll. `rootMargin: '-24px 0 0 0'` shifts the trigger line
 * down to the bar's sticky offset on md+, so elevation engages right
 * when the bar would visually start sticking.
 *
 * No `backdrop-filter`: it requires GPU compositing and silently
 * no-ops in browsers / configurations with hardware acceleration off.
 * A solid background + 1px border + diffuse box-shadow conveys the
 * same "elevated chrome" cue and renders identically in software.
 *
 * Negative horizontal margin (`mx: -2`) negates the outer `DETAIL_SX`
 * padding so the bar spans the full article column width and its border
 * reads as architectural chrome, not as part of the content box.
 */
function StickyArticleHeader({
  title,
  categoryName,
  categoryColor,
  isDraft,
  visibility,
  authorName,
  updatedAt,
  isAdmin,
  onHistory,
  onEdit,
}: {
  title: string;
  categoryName?: string;
  categoryColor?: string | null;
  isDraft: boolean;
  visibility?: string | null;
  authorName?: string | null;
  updatedAt: string | null;
  isAdmin: boolean;
  onHistory: () => void;
  onEdit: () => void;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      // Shrink the IO viewport top by 24px to match the bar's md+ sticky
      // offset. The sentinel crossing this shrunk top edge is equivalent
      // to "the bar has just become stuck". On xs the bar sticks at
      // top:0, so this fires ~24px before stickiness — not noticeable.
      { rootMargin: "-24px 0px 0px 0px", threshold: [0] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Zero-height sentinel — see component comment for the IO pattern. */}
      <Box ref={sentinelRef} aria-hidden sx={{ height: 0 }} />
      <Box
        sx={{
          // Sticks at top:24 on md+ to align horizontally with the rail.
          // The naive version of this leaves a 24px viewport gap above
          // the bar through which body content briefly flashes as it
          // scrolls up. The `::before` below extends the bar's chrome
          // upward by 24px to cover that gap when stuck — see the
          // component-level comment for why it's a pseudo-element and
          // not a separate sibling.
          position: "sticky",
          top: { xs: 0, md: 24 },
          zIndex: 10,
          // Span the full column width — sit outside the article's px: 2.
          mx: -2,
          px: 2,
          pt: 1.25,
          pb: 1.25,
          mb: 1,
          // Background is always opaque — same color as the page bg, so
          // visually identical to "transparent" when nothing is scrolling
          // behind it (i.e., at scroll=0). Transitioning the bg from
          // transparent → opaque was creating a visible flash of content
          // through the bar on fast scrolls; an always-opaque bg blocks
          // that content immediately. The "elevated chrome" cue lives in
          // the border + shadow instead, which can still transition
          // without exposing content.
          backgroundColor: "#f5f5f5",
          borderBottom: stuck
            ? "1px solid #e0e0e0"
            : "1px solid transparent",
          boxShadow: stuck ? "0 2px 8px rgba(0, 0, 0, 0.04)" : "none",
          transition:
            "border-color 180ms ease, box-shadow 180ms ease",
          // Shield: a 24px-tall pseudo-element rendered directly above
          // the bar (only on md+ where the bar sticks at top:24). Same
          // always-opaque background as the bar, so the two read as
          // one continuous opaque strip from viewport y=0 to the bar's
          // bottom border. On xs the bar sticks at top:0, so no shield
          // is needed.
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            top: { xs: 0, md: -24 },
            height: { xs: 0, md: 24 },
            backgroundColor: "#f5f5f5",
            pointerEvents: "none",
          },
        }}
      >
        {/* Two cells: title+byline stack on the left, action buttons
            vertically centered against both lines on the right. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ minWidth: 0 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Line 1: title + chips */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ minWidth: 0 }}
            >
              <Typography
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.15rem",
                  lineHeight: 1.3,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                  // Flex shrink so the title gives up space first when
                  // the row is tight (chips stay legible at narrow widths).
                  flex: "0 1 auto",
                }}
              >
                {title}
              </Typography>
              {categoryName && (
                <Chip
                  label={categoryName}
                  size="small"
                  sx={{
                    height: 20,
                    borderRadius: "3px",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    letterSpacing: "0.3px",
                    color: "#fff",
                    backgroundColor: categoryColor ?? "#6F6F6F",
                    textTransform: "uppercase",
                    flexShrink: 0,
                  }}
                />
              )}
              {isDraft && (
                <Chip
                  label={`Draft · ${visibility ?? ""}`}
                  size="small"
                  sx={{
                    height: 20,
                    borderRadius: "3px",
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    backgroundColor: "#f0f0f0",
                    color: "#666",
                    textTransform: "uppercase",
                    flexShrink: 0,
                  }}
                />
              )}
            </Stack>

            {/* Line 2: byline + timestamp. Hidden if neither is present. */}
            {(authorName || updatedAt) && (
              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  mt: 0.25,
                  color: "text.secondary",
                  fontSize: "0.78rem",
                  flexWrap: "wrap",
                }}
              >
                {authorName && <span>By {authorName}</span>}
                {updatedAt && (
                  <span>
                    {isDraft ? "Last edited" : "Published"}{" "}
                    {formatLongDate(updatedAt)}
                  </span>
                )}
              </Stack>
            )}
          </Box>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            <Tooltip title="Version history" arrow>
              <IconButton size="small" onClick={onHistory}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isAdmin && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={onEdit}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                Edit
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </>
  );
}
